import { filter } from 'rxjs';
import { Injectable, effect, inject, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Dialog } from '../../../core/services/dialog';
import { Platform } from '../../../core/services/platform';
import { LOCAL_ROUTES } from '../../../core/constants/local-routes.constants';
import { UserState } from './user.state';
import { isCourseEnrollUrl } from '../../auth/utils/auth-redirect.util';
import { ProfileDemographicModal } from '../components/profile-demographic-modal/profile-demographic-modal';
import { ProfileProfessionalModal } from '../components/profile-professional-modal/profile-professional-modal';

@Injectable({ providedIn: 'root' })
export class ProfilePromptState {
  private dialog = inject(Dialog);
  private platform = inject(Platform);
  private router = inject(Router);
  private userState = inject(UserState);

  private opened = false;

  constructor() {
    effect(() => {
      const user = this.userState.user();
      const loading = this.userState.loading();
      if (loading || !user) return;
      untracked(() => this.maybeOpen());
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.maybeOpen());
  }

  /**
   * Abre el modal de perfil pendiente (profesional antes que demográfico),
   * como máximo una vez por sesión de la app. No se abre en auth, en el aula
   * ni en la matrícula automática; aparece al navegar a cualquier otra página.
   */
  maybeOpen() {
    const url = this.targetUrl();
    if (url.startsWith(LOCAL_ROUTES.AUTH)) return;
    if (url.startsWith('/classroom')) return;
    if (isCourseEnrollUrl(url)) return;
    this.openPending();
  }

  private openPending(): boolean {
    if (this.opened || this.platform.isServer || this.dialog.hasOpen()) {
      return false;
    }
    const user = this.userState.user();
    if (!user) return false;

    if (user.usuario_perfil_profesional_pendiente) {
      this.opened = true;
      this.dialog.open(ProfileProfessionalModal).subscribe(result => {
        if (result !== null) return;
        this.userState.snoozeProfile('profesional').subscribe();
      });
      return true;
    }

    if (user.usuario_perfil_demografico_pendiente) {
      this.opened = true;
      this.dialog.open(ProfileDemographicModal).subscribe(result => {
        if (result !== null) return;
        this.userState.snoozeProfile('demografico').subscribe();
      });
      return true;
    }

    return false;
  }

  /** URL de destino si hay una navegación en curso; si no, la actual. */
  private targetUrl(): string {
    const navigation = this.router.currentNavigation();
    if (!navigation) return this.router.url;
    return this.router.serializeUrl(
      navigation.finalUrl ?? navigation.extractedUrl
    );
  }
}
